import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup
} from "@angular/forms";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from "@angular/material-moment-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { SortDirection } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import moment from "moment";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UnixToMomentDatePipe } from "src/app/_helpers/pipes/unix-to-moment-date.pipe";
import { usStatesIncludingSpecialRegions, usStatesList } from "src/app/_helpers/utility/us-states-list";
import {
  ILegacyIncidentSearchFilter,
  ILegacyIncidentSearchFilterForm,
} from "src/app/_interfaces/ILegacyIncidentFilter";
import { jurisdiction_list, LegacyAgency, LegacyGeographicArea, LegacyJurisdictionalUnit, LegacyProtectingUnit } from "src/app/_models/reports/legacy";
import { LegacyIncident, LegacyIncidentQueryParams, LegacyIncidentService } from "src/app/_services/map/data-studio/legacy-incident.service";
import { LegacyService } from "src/app/_services/reports/legacy.service";
import { irwinIdValidator } from "src/app/_validators/irwin-id-validator";
import { LegacyIncidentDownloadDialogComponent } from "../legacy-incident-download-dialog/legacy-incident-download-dialog.component"
import { ExportService } from "src/app/_services/shared/export.service";

@Component({
    selector: "wfdss-legacy-incident-search",
    templateUrl: "./legacy-incident-search.component.html",
    styleUrls: ["./legacy-incident-search.component.scss"],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: "l",
                },
                display: {
                    dateInput: "l",
                    monthYearLabel: "MMM YYYY",
                    dateA11yLabel: "LL",
                    monthYearA11yLabel: "MMMM YYYY",
                },
            },
        }
    ],
    standalone: false
})
export class LegacyIncidentSearchComponent implements OnInit, OnDestroy {
  
  @Output() filterUpdatedEvent: EventEmitter<ILegacyIncidentSearchFilter> =
    new EventEmitter();

  incidentFilterForm = new FormGroup<ILegacyIncidentSearchFilterForm>({
    incidentName: new FormControl(""),
    irwinId: new FormControl("", [irwinIdValidator]),
    incidentSizeType: new FormControl(),
    startSize: new FormControl(),
    endSize: new FormControl(),
    estimatedFinalCostType: new FormControl(""),
    finalCost: new FormControl(),
    finalCostEnd: new FormControl(),
    year: new FormControl("", [this.isValidSearchYear]),
    exactMatch: new FormControl(true),
    anyDiscoveryDate: new FormControl("anyDiscoveryDate"),
    discoveryDate: new FormControl(false),
    anyOutDate: new FormControl("anyOutDate"),
    noOutDate: new FormControl(false),
    discoveryDateRangeType: new FormControl({ value: "On", disabled: true }),
    outDateRangeType: new FormControl({ value: "On", disabled: true }),
    discoveryDateLower: new FormControl({value: "2020-01-01", disabled: true}),
    outDateLower: new FormControl({ value: "2020-01-01", disabled: true }),
    discoveryDateUpper: new FormControl({ value: "2020-01-01", disabled: true }),
    outDateUpper: new FormControl({ value: "2020-01-01", disabled: true }),
    selectedGeographicAreaId: new FormControl(-1),
    selectedState: new FormControl("-1"),
    selectedAgencyId: new FormControl(-1),
    selectedJurisditionalUnitIds: new FormControl([-1]),        
    selectedProtectingUnitIds: new FormControl([-1]),        
    publishedDecision: new FormControl(false),
    completedAnalysis: new FormControl(false),

  });
  
  geographicAreas: LegacyGeographicArea[] = [];
  statesPickList = usStatesIncludingSpecialRegions;
  agencies: LegacyAgency[] = [];
  jurisdictionalUnits: LegacyJurisdictionalUnit[] = [];
  protectingUnits: LegacyProtectingUnit[] = []; 

  selectedJurisdictionalUnits: LegacyJurisdictionalUnit[] = [];
  selectedProtectingUnits: LegacyProtectingUnit[] = [];

  dateRangeValues: Array<ILegacyIncidentSearchFilter["discoveryDateRangeType"]> = [
    "On",
    "Before",
    "After",
    "Between",
    "No Selection"
  ];
  numberRangeLOV: Array<{value: ILegacyIncidentSearchFilter["incidentSizeType"], label: string}> = [
    {value: "Less than", label: "Less than or equal to"},
    {value: "Greater than", label: "Greater than or equal to"},
    {value: "Between", label: "Between"},
    {value: "No Selection", label: "No Selection"}
  ];

  dateFilterOptions = [
    {
      mainFormControlName: "anyDiscoveryDate",
      labelForAny: "Any Discovery Date",
      labelForRestricted: "Discovered",
      rangeFormControlName: "discoveryDateRangeType",
      lowerDateFormControlName: "discoveryDateLower",
      upperDateFormControlName: "discoveryDateUpper",
    },
    {
      mainFormControlName: "anyOutDate",
      labelForAny: "Any Out Date",
      labelForRestricted: "Out",
      labelForNoDate: "No Out Date",
      rangeFormControlName: "outDateRangeType",
      lowerDateFormControlName: "outDateLower",
      upperDateFormControlName: "outDateUpper",
    },
  ];
  geoAreaIdChangeSubscription: Subscription;
  agencyChangeSubscription: Subscription;
  unitChangeSubscription: Subscription;
  legacyIncidentDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  noJurisdictionSelection = true;
  noProtectingUnitSelection = true;

  public _destroying = new Subject<void>();

  //TODO: For typeahead feature for jurisdictional units
  // jdtnSearchControl = new FormControl();
  // filteredJdtnUnits$: Observable<LegacyJurisdictionalUnit[]>;
  // lastFilter: string = '';

  sortActive = "Incident Name";
  sortDirection = "asc" as SortDirection

  sortingDataAccessor = (data: any, prop) => {
    switch (prop) {
      case "Incident Name": 
        return data.incidentName.toLowerCase();
      case "Unique Fire Identifier":
        return data.uniqueFireId;
      case "Geographic Area":
        return data.geographicAreaName;
      case "Jurisdiction(s)":
        return jurisdiction_list[data.jurisdictions + ''];
      case "Protecting Unit":
        return data.responsibleUnit + ' - ' + data.responsibleUnitName;
      case "State":
        return data.state
      case "Size (Acres)":          
        return data.acreage;
      case "Discovery Date/Time": 
        return data.discoveredDate.unix()
      }
  }  

  CSVExportDataAccessor = (data:any, prop) =>{
    switch (prop) {
      case "Incident Name": 
        return data.incidentName;
      case "Unique Fire Identifier":
        return data.uniqueFireId;
      case "Geographic Area":
        return data.geographicAreaName;
      case "Jurisdiction(s)":
        return jurisdiction_list[data.jurisdictions + ''];
      case "Protecting Unit":
        return data.responsibleUnit + ' - ' + data.responsibleUnitName;
      case "State":
        return data.state
      case "Size (Acres)":          
        return data.acreage;
      case "Discovery Date/Time": 
        return data.discoveredDate.format("MM/DD/YYYY, HH:mm")
    }
  }
  constructor(
    private legacyService: LegacyService, 
    private fb: FormBuilder, 
    private lis: LegacyIncidentService,
    private dialog: MatDialog,
    private renderer: Renderer2,
    private exportSvc: ExportService
  ) { }

  ngAfterViewInit(): void {
    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;

       if (target.closest('mat-mdc-dialog-container')) return; 
       
      const rowElement = target.closest('tr.mat-mdc-row');
      if (!rowElement) return;

      if (this.dialog.openDialogs.length > 0) return; 
      
      const cells = rowElement.querySelectorAll('td');
      if (!cells || cells.length < 2) return;

      const uniqueFireIdCell = cells[1].textContent?.trim(); // Assuming Unique Fire Identifier is in the second column (index 1)
      if (!uniqueFireIdCell) return;

      const rowData = Array.from(this.legacyIncidentDataSource.data).find(row => {
        return row.uniqueFireId === uniqueFireIdCell;
      });

      if (!rowData?.wfdssId) return;

      this.openDownloadDialog(rowData);
   }) }

  ngOnInit() {
    this.legacyService
      .getLegacyGeographicAreas()
      .toPromise()
      .then((geoAreas) => (this.geographicAreas = geoAreas));

    this.legacyService
      .getLegacyJurisdictionalAgencies()
      .toPromise()
      .then((agencies) => (this.agencies = agencies));
      
    this.legacyService
      .getLegacyJurisdictionalUnits()
      .toPromise()
      .then((jUnits) => {          
          this.jurisdictionalUnits = jUnits;
          //TODO: For typeahead feature for jurisdictional units
          // this.filteredJdtnUnits$ = of(jUnits);
        });

    this.legacyService
      .getLegacyProtectingUnits()
      .toPromise()
      .then((protectingUnits) => (this.protectingUnits = protectingUnits));


    this.incidentFilterForm.controls.selectedJurisditionalUnitIds.valueChanges.pipe(takeUntil(this._destroying)).subscribe(
      (selectedUnitIds) => {
        if(selectedUnitIds.includes(-1)) {
          this.noJurisdictionSelection = true;
          this.incidentFilterForm.controls.selectedJurisditionalUnitIds.setValue([-1], {emitEvent: false});
          this.selectedJurisdictionalUnits = [];
          return;
        }
        this.noJurisdictionSelection = false;
        this.selectedJurisdictionalUnits = this.jurisdictionalUnits?.filter(each => selectedUnitIds.includes(each.unitId));
      })

    this.incidentFilterForm.controls.selectedProtectingUnitIds.valueChanges.pipe(takeUntil(this._destroying)).subscribe(
      (selectedUnitIds) => {
        if(selectedUnitIds.includes(-1)) {
          this.noProtectingUnitSelection = true;
          this.incidentFilterForm.controls.selectedProtectingUnitIds.setValue([-1], {emitEvent: false});
          this.selectedProtectingUnits = [];
          return;
        }
        this.noProtectingUnitSelection = false;
        this.selectedProtectingUnits = this.protectingUnits?.filter(each => selectedUnitIds.includes(each.unitId));
      })
    this.incidentFilterForm.get('incidentSizeType').valueChanges.pipe(takeUntil(this._destroying)).subscribe((vc)=>{
      if(vc =="No Selection"){
        this.incidentFilterForm.get('startSize').setValue(null, {emitEvent: false})
        this.incidentFilterForm.get('startSize').disable()
      } else {
        this.incidentFilterForm.get('startSize').enable()
      }
    })
    this.incidentFilterForm.get('estimatedFinalCostType').valueChanges.pipe(takeUntil(this._destroying)).subscribe((vc)=>{
      if(vc =="No Selection"){
        this.incidentFilterForm.get('finalCost').setValue(null, {emitEvent: false})
        this.incidentFilterForm.get('finalCost').disable()
      } else {
        this.incidentFilterForm.get('finalCost').enable()
      }
    })

    //TODO: For typeahead feature for jurisdictional units
      
    // this.jdtnSearchControl.valueChanges.pipe(takeUntil(this._destroying)).subscribe(
    //   (searchTxt) => {
    //     searchTxt = (searchTxt ?? '').trim().toLowerCase();
    //     const filteredList = this.jurisdictionalUnits.filter( 
    //       each => {
    //         const include = each.name.toLowerCase().includes(searchTxt)
    //         return include; 
    //       }
    //     );
    //     this.filteredJdtnUnits$ = of(filteredList);
    //   }
    // );

  }

  onClickSearch() { }

  ngOnDestroy() {
    this.geoAreaIdChangeSubscription &&
      this.geoAreaIdChangeSubscription.unsubscribe();

    this.agencyChangeSubscription &&
      this.agencyChangeSubscription.unsubscribe();

    this.unitChangeSubscription && this.unitChangeSubscription.unsubscribe();

    this._destroying.next();
    this._destroying.complete();
  }

  onSearch() {
    const incidentSize1 = this.incidentFilterForm.value.startSize;
    const incidentSize2 = this.incidentFilterForm.value.endSize;
    const estimatedCost1 = this.convertDollarToNumber(this.incidentFilterForm.value.finalCost);
    const estimatedCost2 = this.convertDollarToNumber(this.incidentFilterForm.value.finalCostEnd);
    const discoveryDate1 = new Date(this.incidentFilterForm.value.discoveryDateLower);
    const discoveryDate2 = new Date(this.incidentFilterForm.value.discoveryDateUpper);
    const outDate1 = new Date(this.incidentFilterForm.value.outDateLower);
    const outDate2 = new Date(this.incidentFilterForm.value.outDateUpper);


    let whereParams: LegacyIncidentQueryParams;
    if(this.incidentFilterForm.value.irwinId) {
      // When irwinId is provided in filter, ignore all other filter parameters
      whereParams = {
        irwinId: this.incidentFilterForm.value.irwinId
      }
    } else {
      whereParams = {
              usePartialMatch: true,
              incidentName: this.incidentFilterForm.value.incidentName || null,
              discoveredDateOn: this.incidentFilterForm.value.discoveryDateRangeType === 'On'
                                ? discoveryDate1 : null,
              discoveredDateMin: ['After', 'Between'].includes(this.incidentFilterForm.value.discoveryDateRangeType)
                                ? discoveryDate1 : null,
              discoveredDateMax: this.incidentFilterForm.value.discoveryDateRangeType == 'Before'
                                ? discoveryDate1 
                                : (this.incidentFilterForm.value.discoveryDateRangeType == 'Between'? discoveryDate2 : null),
              outDateOn: this.incidentFilterForm.value.outDateRangeType === 'On'
                                ? outDate1 : null,
              outDateMin: ['After', 'Between'].includes(this.incidentFilterForm.value.outDateRangeType)
                                ? outDate1 : null,
              outDateMax: this.incidentFilterForm.value.outDateRangeType === 'Before'
                          ? outDate1 
                          : (this.incidentFilterForm.value.outDateRangeType === 'Between' ? outDate2 : null),
              outDateNotOut: this.incidentFilterForm.value.noOutDate || null,
              geographicAreaName: this.geographicAreas?.find(each => each.id === this.incidentFilterForm.value.selectedGeographicAreaId)?.geographicAreaName,
              agency: this.agencies.find(each => each.id === this.incidentFilterForm.value.selectedAgencyId)?.agency,
              responsibleUnits: this.protectingUnits.filter(each => this.incidentFilterForm.value.selectedProtectingUnitIds?.includes(each.unitId))?.map(each => each.unitId + ''),
              incidentSizeAcresMin: ['Greater than', 'Between'].includes(this.incidentFilterForm.value.incidentSizeType)
                                ? incidentSize1 : null,
              incidentSizeAcresMax: this.incidentFilterForm.value.incidentSizeType === 'Less than'
                                ? incidentSize1 
                                : (this.incidentFilterForm.value.incidentSizeType === 'Between' ? incidentSize2 : null),
              estimatedPublishedFinalCostMin: ['Greater than', 'Between'].includes(this.incidentFilterForm.value.estimatedFinalCostType)
                                ? estimatedCost1 : null,
              estimatedPublishedFinalCostMax: this.incidentFilterForm.value.estimatedFinalCostType === 'Less than'
                                ? estimatedCost1
                                : (this.incidentFilterForm.value.estimatedFinalCostType === 'Between'? estimatedCost2 : null),
              state: this.incidentFilterForm.value.selectedState!=='-1'?this.incidentFilterForm.value.selectedState : null,
              jurisdictionalUnits: this.jurisdictionalUnits.filter(each => this.incidentFilterForm.value.selectedJurisditionalUnitIds?.includes(each.unitId))?.map(each => each.unitId + ''),
              hasPublishedDecision: this.incidentFilterForm.value.publishedDecision || null,
              hasCompletedAnalysis: this.incidentFilterForm.value.completedAnalysis || null   
          }
    }

    console.log("whereParams", whereParams)
    
   

    this.lis.queryLegacyIncidents(whereParams).then((result: LegacyIncident[]) => {
      console.log(result);
      this.legacyIncidentDataSource.data = result.map(item => ({
        ...item,
        discoveredDate: moment(item.discoveredDate).utc(),
        outDate: moment(item.outDate).utc()
      }));
    })

    // alert(
    //   "Search feature in progress. See console (F12) for form filter state"
    // );
    // console.log(this.incidentFilterForm.value, "Form Filter state");
  }

  convertDollarToNumber(dollar) {
    if(!dollar) return null;

    dollar = dollar.replace(/\$|,/g, '')
    const number = parseFloat(dollar);
    return number;
  }

  radioSelectionChange() {
    const anyDiscoveryCtrl = this.incidentFilterForm.get('anyDiscoveryDate')?.value;
    const anyOutDateCtrl = this.incidentFilterForm.get('anyOutDate')?.value;
    if (anyDiscoveryCtrl !== 'Discovered') {
      this.incidentFilterForm.get('discoveryDateRangeType').disable();
      this.incidentFilterForm.get('discoveryDateLower').disable();
      this.incidentFilterForm.get('discoveryDateUpper').disable();
    } else {
      this.incidentFilterForm.get('discoveryDateRangeType').enable();
      this.incidentFilterForm.get('discoveryDateLower').enable();
      this.incidentFilterForm.get('discoveryDateUpper').enable();
    }
    if (anyOutDateCtrl !== 'Out') {
      this.incidentFilterForm.get('outDateRangeType').disable();
      this.incidentFilterForm.get('outDateLower').disable();
      this.incidentFilterForm.get('outDateUpper').disable();
    } else {
      this.incidentFilterForm.get('outDateRangeType').enable();
      this.incidentFilterForm.get('outDateLower').enable();
      this.incidentFilterForm.get('outDateUpper').enable();
    }  
  }

  isValidSearchYear(control: AbstractControl) {
    return control.value >= 2007 ? null : { invalidYear: true };
  }

  onTableWrapperClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const rowElement = target.closest('tr');
    if (!rowElement || !rowElement.classList.contains('mat-header-row')) return;

    const tbody = rowElement.parentElement;
    if (!tbody) return; 

    const rows = Array.from(tbody.children);
    const index = rows.indexOf(rowElement);

    if (index < 0) return;
    
    const rowData = this.legacyIncidentDataSource.data[index];
    if (!rowData) return;

    const wfdssId = rowData.wfdssId;
    if (!wfdssId) return;

    this.openDownloadDialog(rowData);
   }

   openDownloadDialog(row: any): void {
    this.dialog.open(LegacyIncidentDownloadDialogComponent, {
      width: '700px',
      data: { 
        wfdssId: row.wfdssId,
        incidentName: row.incidentName, 
        uniqueFireIdentifier: row.uniqueFireId
      }
    }); 
   }

  public legacyIncidentDisplayConfig = [
    {
      columnName: "Incident Name",
      propertyAccessor: (prop) => {
        return prop.incidentName;
      },
    },
    {
      columnName: "Unique Fire Identifier",
      propertyAccessor: (prop) => {
        return prop.uniqueFireId;
      },
    },
    {
      columnName: "Geographic Area",
      propertyAccessor: (prop) => {
        return prop.geographicAreaName;
      },
    },
    {
      columnName: "Jurisdiction(s)",
      propertyAccessor: (prop) => {
        return jurisdiction_list[prop.jurisdictions + ''];
      },
    },
    {
      columnName: "Protecting Unit",
      propertyAccessor: (prop) => {
        return prop.responsibleUnit + ' - ' + prop.responsibleUnitName;
      },
    },
    {
      columnName: "State",
      propertyAccessor: (prop) => {
        return prop.state;
      },
    },
    {
      columnName: "Size (Acres)",
      propertyAccessor: (prop) => {
        return prop.acreage;
      }      
    },
    {
      columnName: "Discovery Date/Time",
      propertyAccessor: (prop) => {
        return prop.discoveredDate.format("MM/DD/YYYY, HH:mm")
      }
    },
    // {
    //   columnName: "Status",
    //   propertyAccessor: (prop) => {
    //     return ''
    //   },
    // },
    // {
    //   columnName: "Type",
    //   propertyAccessor: (prop) => {
    //     return ''
    //   },
    // }
  ];

}
